"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import {
  CHAT_ATTACHMENTS_BUCKET,
  chatAttachmentPath,
  collectChatAttachmentPaths,
} from "@/lib/chat/chat-attachment-url";
import { ChatAttachmentSigner } from "@/lib/chat/chat-attachment-signer";

// locarora-admin `lib/hooks/useSignedChatAttachmentUrls.ts` 와 같은 내용을 유지한다.

type StorageClient = Pick<SupabaseClient, "storage">;

/**
 * chat-attachments(private 버킷) 첨부를 화면에 그리기 위한 서명 URL.
 *
 * messages 의 attachments(type image|file) 중 아직 서명하지 않은 경로를 모아
 * 브라우저 Supabase 클라이언트로 일괄 서명한다(SELECT RLS = 채팅방 참여자만).
 * 초기 번들·이전 메시지 로드·realtime INSERT/UPDATE·전송 직후 메시지 모두
 * `messages` 배열에 들어오기만 하면 자동으로 서명된다.
 *
 * - resolve(url): 서명 URL / chat-attachments 가 아니면 원래 URL /
 *   아직 서명 전이면 undefined (호출부는 같은 크기의 자리표시자를 그린다).
 * - openAttachment(url, name): 한 경로를 새로 서명(60초, 다운로드 파일명)해 새 탭으로 연다.
 *   아직 서명 전인 파일 링크를 누른 경우의 폴백.
 */
export function useSignedChatAttachmentUrls(
  messages: ReadonlyArray<{ attachments?: unknown } | null | undefined>,
  client: StorageClient,
) {
  // client 는 모듈 싱글턴(createClient)이라 처음 받은 것을 계속 쓴다.
  const [signer] = useState(
    () =>
      new ChatAttachmentSigner(async (paths, expiresIn) => {
        const { data, error } = await client.storage
          .from(CHAT_ATTACHMENTS_BUCKET)
          .createSignedUrls(paths, expiresIn);
        if (error || !data) throw error ?? new Error("sign failed");
        return data;
      }),
  );

  // 렌더를 다시 일으키는 카운터: 서명 완료(version) / 재확인 타이머·탭 복귀(tick).
  const [version, setVersion] = useState(0);
  const [tick, setTick] = useState(0);

  const paths = useMemo(() => collectChatAttachmentPaths(messages), [messages]);
  // messages 배열은 메시지마다 새로 만들어지므로 경로 목록 문자열로 의존성을 고정한다.
  const pathsKey = paths.join("\n");

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const current = pathsKey ? pathsKey.split("\n") : [];
    const need = signer.pathsNeedingSign(current);
    if (need.length > 0) {
      void signer.sign(need).then((changed) => {
        if (changed && mountedRef.current) setVersion((v) => v + 1);
      });
    }
    const next = signer.nextCheckAt(current);
    if (next === null) return;
    const timer = setTimeout(
      () => setTick((x) => x + 1),
      Math.max(1000, next - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [signer, pathsKey, version, tick]);

  // 백그라운드 탭에선 타이머가 늦게 돌 수 있다 → 복귀 시 즉시 재확인.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") setTick((x) => x + 1);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  // version/tick 이 바뀌면 resolve 의 identity 도 바뀌어 자식이 다시 그려진다.
  const resolve = useMemo(() => {
    void version;
    void tick;
    return (url: string) => signer.resolve(url);
  }, [signer, version, tick]);

  const openAttachment = useCallback(async (url: string, name?: string) => {
    const path = chatAttachmentPath(url);
    let target = url;
    if (path) {
      try {
        const { data, error } = await client.storage
          .from(CHAT_ATTACHMENTS_BUCKET)
          .createSignedUrl(path, 60, name ? { download: name } : undefined);
        target = !error && data?.signedUrl ? data.signedUrl : signer.resolve(url) ?? url;
      } catch {
        target = signer.resolve(url) ?? url;
      }
    }
    window.open(target, "_blank", "noopener,noreferrer");
  }, [client, signer]);

  return { resolve, openAttachment };
}
